<template>
    <div v-if="template" class="tw-bg-gray-100 tw-flex tw-flex-col" id="template-details" :class="{'modal-preview':templatePreview}">
        <div class="header">
            <div v-if="!templatePreview" class="breadcrumbs" id="breadcrumbs">
                <router-link class="breadcrumb-link" to="/surveys/dashboard">Bookblock HUB Surveys</router-link> &gt;
                <router-link class="breadcrumb-link" to="/surveys/templates">Survey Templates</router-link> &gt;
                <router-link class="breadcrumb-link" :to="'/surveys/templates' + template.id">{{ template.name }}</router-link>
            </div>
            <h3>{{ template.name }} 
                <template v-if="templatePreview">
                    <span>Preview</span>
                    <div class="ws-button ws-button-main" @click="$emit('activate',template.id)">Activate This Template</div>
                    <router-link v-if="isAdmin" :to="{ name: 'surveys-template-details', params: { template_id: template.id }}" class="ws-button ws-button-admin" @click="$emit('activate',template.id)">Edit Template</router-link>
                </template>
            </h3>
            
        </div>
        <div class="template-detail tw-flex tw-bg-white tw-h-full tw-p-4 tw-flex-grow questions">
            <div class="left-bar tw-flex tw-flex-col">
                <h5 class="tw-text-base"><b>Pages</b> ({{ pages.length }})</h5>
                <draggable v-model="pageIds" :disabled="displayMode !== 'editor'" class="pages-list tw-overflow-auto">
                    <div class="page tw-p-2 tw-flex tw-justify-between tw-items-center tw-group"
                            v-for="page in pages" :key="page.id"
                            :class="{'active': page.id === selectedPage.id}"
                            :data-icon="page.question_type"
                            @click="selectPage(page)">
                        <span>{{ page.name }}</span>
                        <span v-if="displayMode === 'editor'"
                            class="ws-button edit tw-cursor-pointer"
                            @click="onEditPage(page)">
                        </span>
                    </div>
                </draggable>
                <div class="tw-mt-auto tw-text-center">
                    <modal v-if="showEditModal" @close="showEditModal = false">
                        <page-settings-form store-name="surveyTemplatePages" :page="selectedPage" @save="onUpdate" @delete="onDelete" @duplicate="onDuplicate" 
                            @close="showEditModal = false" />
                    </modal>
                    <modal v-if="showCreateModal" @close="showCreateModal = false" :width="750">
                        <new-page-form store-name="surveyTemplatePages" @save="onCreate"/>
                    </modal>
                    <modal v-if="showActivateModal" @close="showActivateModal = false">
                        <survey-settings-form
                                :survey="template"
                                store-name="surveyTemplatePages"
                                @save="onActivate"
                                @close="showActivateModal = false"/>
                    </modal>
                    <modal v-if="showSettingsModal" @close="showSettingsModal = false">
                        <template-settings-form
                                :template="template"
                                @save="onUpdateTemplate"
                                @cancel="showSettingsModal = false"/>
                    </modal>
                    <div v-if="displayMode === 'editor'" class="ws-button ws-button-main add-new tw-w-full"
                         @click="showCreateModal = true">Add New Page
                    </div>
                    <div v-if="!templatePreview" class="ws-button add-new tw-w-full mt-2"
                         @click="showSettingsModal = true">Template Settings
                    </div>
                    <div v-if="!isAdmin" class="ws-button tw-w-full" @click="showActivateModal = true">Activate</div>
                </div>
            </div>
            
            <div class="center-bar tw-rounded-lg" :class="{'preview-mode': templatePreview}">
                <div class="inner tw-h-full" v-if="selectedPage.id">
                    <page-view
                        :page="selectedPage"
                        :styles="template.theme?.styles"
                        :editable="displayMode === 'editor'"
                        readonly
                        view="editor"
                        store-prefix="surveyTemplate"
                    />
                </div>

                <div v-if="!pages.length && !templatePreview"
                     class="tw-p-2 tw-h-full tw-text-center tw-flex tw-justify-center tw-items-center">
                    <p>Your survey template doesn't have any pages.<span class="ws-button ws-button-main add-new" style="display: block; margin-top: 10px" @click="showCreateModal = true">Add Your First Page</span> </p>
                </div>
                <div v-else-if="!selectedPage.id && !templatePreview"
                     class="tw-p-2 tw-h-full tw-text-center tw-flex tw-justify-center tw-items-center">
                    <p>Select a page to start editing.</p>
                </div>
                <div class="modes tw-flex tw-gap-2" v-if="!templatePreview">
                    <div class="ws-button tw-flex-1 tw-text-center" :class="{ 'active': displayMode === 'editor' }"
                         @click="displayMode = 'editor'">Editor Mode
                    </div>
                    <div class="ws-button tw-flex-1 tw-text-center" :class="{ 'active': displayMode === 'preview' }"
                         @click="displayMode = 'preview'">Preview Mode
                    </div>
                </div>
            </div>
            <survey-settings-bar v-if="!templatePreview" :parent="template" store-prefix="surveyTemplate" />
        </div>
    </div>
</template>

<script>

import PageView from "@/pages/surveys/PageView";
import NewPageForm from "@/pages/surveys/NewPageForm";
import PageSettingsForm from "@/pages/surveys/PageSettingsForm";
import Modal from "@/components/modals/Modal";
import draggable from "vuedraggable";
import SurveySettingsForm from "@/pages/surveys/SurveySettingsForm";
import TemplateSettingsForm from "@/pages/surveys/TemplateSettingsForm";
import SurveySettingsBar from "@/pages/surveys/SurveySettingsBar.vue";

export default {
    name: 'TemplateDetails',
    components: {SurveySettingsBar, TemplateSettingsForm, SurveySettingsForm, PageView, NewPageForm, PageSettingsForm, Modal, draggable},
    data() {
        return {
            showCreateModal: false,
            showEditModal: false,
            showSettingsModal: false,
            showActivateModal: false,
            displayMode: 'preview',
        }
    },
    props: {
        templatePreview: {
            type: Number,
            default: undefined,
        },
    },
    computed: {
        samplePage() {
            return this.$store.state.surveyThemeSamplePageSmall;
        },
        template() {
            return this.$store.getters["surveyTemplates/selected"];
        },
        pageIds: {
            get() {
                return this.$store.state.surveyTemplatePages.ids;
            },
            set(ids) {
                this.$store.dispatch('surveyTemplatePages/reorder', ids.filter(id => id))
            },
        },
        selectedPage() {
            return this.$store.getters["surveyTemplatePages/selected"] || {};
        },
        pages() {
            return this.$store.getters["surveyTemplatePages/orderedList"];
        },
        themes() {
            return this.$store.getters["surveyThemesGallery/orderedList"];
        },
        isAdmin() {
            return this.$store.state.currentUser.is_bb_admin;
        },
        coreLoaded() { return this.$store.state.coreLoaded }
    },
    beforeRouteUpdate(to, from, next) {
        this.fetchTemplate(to.params.template_id);
        next();
    },
    created() {
        if (!this.templatePreview) {
            this.displayMode = 'editor';
        }
        this.fetchTemplate(this.templatePreview ? this.templatePreview : this.$route.params.template_id);
    },
    methods: {
        async fetchTemplate(id) {
            if (this.$store.state.surveyTemplates.selected !== id) {
                this.$store.commit('surveyTemplates/setSelected', id);
                this.$store.dispatch('surveyTemplates/getById', {id});
            }
            if (!this.templatePreview) this.$store.dispatch('surveyThemesGallery/getList', {active: true})

            await this.$store.dispatch('surveyTemplatePages/getList', {})
            if (this.pages.length) {
                await this.selectPage(this.pages[0])
            }
        },
        async selectPage(page) {
            this.$store.commit('surveyTemplatePages/setSelected', page.id);
            page = await this.$store.dispatch('surveyTemplatePages/getById', page.id);

            this.$store.commit('surveyTemplateQuestions/setList', page.questions);
            if (this.selectedPage.questions.length) this.$store.commit('surveyTemplateQuestions/setSelected', this.selectedPage.questions[0].id);
            else this.$store.commit('surveyTemplateQuestions/setSelected', null);
        },
        async onCreate(data) {
            const page = await this.$store.dispatch('surveyTemplatePages/create', data);
            await this.selectPage(page);
            this.showCreateModal = false;
        },
        async onEditPage(page) {
            await this.selectPage(page)
            this.showEditModal = true;
        },
        async onUpdate(data) {
            const page = await this.$store.dispatch('surveyTemplatePages/update', data);
            await this.selectPage(page);
            this.showEditModal = false;
        },
        async onUpdateTemplate(data) {
            await this.$store.dispatch('surveyTemplates/update', data);
            this.showSettingsModal = false;
        },
        async onActivate(payload) {
            const survey = await this.$store.dispatch('surveyTemplates/activate', payload);
            this.showActivateModal = false;
            this.$router.push({name: 'surveys-details', params: {survey_id: survey.id}})
        },
        onNewPageFormClose() {
            this.showCreateModal = false;
        },
        async onDuplicate(data) {
            const page = await this.$store.dispatch('surveyTemplatePages/create', data);
            await this.selectPage(page);
            this.showEditModal = false;
        },
        async onDelete(id) {
            await this.$store.dispatch('surveyTemplatePages/delete', id);
            await this.fetchTemplate(this.template.id);
            if (this.pages.length) await this.selectPage(this.pages[0]);
            this.showEditModal = false;
        },
    }
}
</script>
